/**
 * Lightweight wrapper around `window.Telegram.WebApp` so the rest of the app
 * doesn't need to know about globals or perform constant null checks.
 *
 * We intentionally avoid pulling in heavy SDKs for the basic surface; the
 * official `telegram-web-app.js` is loaded from the CDN in `index.html`.
 */

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    user?: { id: number; first_name: string; last_name?: string; username?: string };
    start_param?: string;
  };
  colorScheme: 'light' | 'dark';
  themeParams: Record<string, string | undefined>;
  ready: () => void;
  expand: () => void;
  close: () => void;
  HapticFeedback?: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
  };
  BackButton: {
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };
  MainButton: {
    text: string;
    show: () => void;
    hide: () => void;
    enable: () => void;
    disable: () => void;
    setText: (text: string) => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
    showProgress: () => void;
    hideProgress: () => void;
  };
  showAlert: (msg: string) => void;
  showConfirm: (msg: string, cb: (ok: boolean) => void) => void;
  onEvent: (event: string, cb: () => void) => void;
  offEvent: (event: string, cb: () => void) => void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export function getWebApp(): TelegramWebApp | null {
  if (typeof window === 'undefined') return null;
  return window.Telegram?.WebApp ?? null;
}

export function getInitData(): string {
  return getWebApp()?.initData ?? '';
}

export function getColorScheme(): 'light' | 'dark' {
  return getWebApp()?.colorScheme ?? 'light';
}

export function applyTelegramTheme(): void {
  const wa = getWebApp();
  if (!wa) return;
  wa.ready();
  wa.expand();
  document.documentElement.classList.toggle('theme-dark', wa.colorScheme === 'dark');

  const tp = wa.themeParams;
  const root = document.documentElement.style;
  const map: Record<string, string> = {
    bg_color: '--tg-bg',
    text_color: '--tg-text',
    hint_color: '--tg-hint',
    link_color: '--tg-link',
    button_color: '--tg-button',
    button_text_color: '--tg-button-text',
    secondary_bg_color: '--tg-secondary-bg',
  };
  for (const [k, cssVar] of Object.entries(map)) {
    const v = tp[k];
    if (!v) continue;
    const triplet = hexToRgbTriplet(v);
    if (triplet) root.setProperty(cssVar, triplet);
  }
}

function hexToRgbTriplet(hex: string): string | null {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 0xff;
  const g = (n >> 8) & 0xff;
  const b = n & 0xff;
  return `${r} ${g} ${b}`;
}
